import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X, ArrowRight } from 'lucide-react';
import { NAV } from '@/constants/testIds';
import { useSiteSettings } from '@/context/SiteSettingsContext';

/* ── Solutions Mega-Menu ─────────────────────────────────────────── */
function SolutionsMenu({ isDark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 py-2 ${
          isDark ? 'text-white/80 hover:text-white' : 'text-[#374151] hover:text-[#0E5A57]'
        }`}
      >
        Solutions
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
          >
            <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl overflow-hidden w-[520px]">
              <div className="grid grid-cols-2">
                {/* India */}
                <Link
                  to="/solutions/india"
                  onClick={() => setOpen(false)}
                  className="group flex flex-col p-6 bg-[#0B3F3B] hover:bg-[#0D4F4A] transition-colors duration-200"
                >
                  <span className="text-[10px] font-semibold tracking-widest uppercase text-white/50 mb-2">India</span>
                  <span className="font-serif text-base font-bold text-white leading-snug mb-2">Digital & Business Solutions</span>
                  <span className="text-white/55 text-xs leading-snug mb-4">Websites, SEO, Design, Admin & E-commerce for Indian businesses.</span>
                  <span className="inline-flex items-center gap-1 text-[#D4AF37] text-xs font-semibold group-hover:gap-2 transition-all duration-150">
                    Explore <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>

                {/* Global */}
                <Link
                  to="/solutions/global"
                  onClick={() => setOpen(false)}
                  className="group flex flex-col p-6 bg-[#0F2847] hover:bg-[#112F56] transition-colors duration-200"
                >
                  <span className="text-[10px] font-semibold tracking-widest uppercase text-white/50 mb-2">Global</span>
                  <span className="font-serif text-base font-bold text-white leading-snug mb-2">Outsourcing & Business Solutions</span>
                  <span className="text-white/55 text-xs leading-snug mb-4">Admin, Customer Ops, Finance, HR & Specialist support for international markets.</span>
                  <span className="inline-flex items-center gap-1 text-[#5CC8C3] text-xs font-semibold group-hover:gap-2 transition-all duration-150">
                    Explore <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              </div>
              <div className="px-4 py-3 bg-[#F7FAFC] border-t border-[#E5E7EB]">
                <Link
                  to="/solutions"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 text-xs font-semibold text-[#0E5A57] hover:underline"
                >
                  View all solutions <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main Navigation ─────────────────────────────────────────────── */
const NAV_LINKS = [
  { label: 'Industries', href: '/industries' },
  { label: 'About', href: '/about' },
  { label: 'Insights', href: '/insights' },
  { label: 'Contact', href: '/contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const siteSettings = useSiteSettings();

  const DARK_HERO_PATHS = ['/', '/contact', '/about', '/solutions', '/solutions/india', '/solutions/global'];
  const isHeroPage = DARK_HERO_PATHS.includes(location.pathname)
    || location.pathname.startsWith('/services/')
    || location.pathname.startsWith('/industries/')
    || location.pathname.startsWith('/case-experience/');
  const isDark = isHeroPage && !scrolled;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); setMobileExpanded(null); }, [location]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'nav-glass shadow-sm' : 'bg-transparent'}`}
        role="banner"
      >
        <nav
          className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-[70px]"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-0 group flex-shrink-0">
            {(isDark ? siteSettings.light_logo_url : siteSettings.primary_logo_url) ? (
              <img
                src={isDark ? siteSettings.light_logo_url : siteSettings.primary_logo_url}
                alt={siteSettings.brand_name || 'NKP VENTURES'}
                className="h-8 w-auto object-contain"
              />
            ) : (
              <>
                <span className={`font-serif font-bold text-[1.2rem] tracking-tight leading-none transition-colors duration-300 ${
                  isDark ? 'text-white' : 'text-[#153E5C]'
                }`}>NKP</span>
                <span className={`ml-2 font-mono text-[0.58rem] tracking-[0.32em] uppercase font-medium leading-none transition-colors duration-300 ${
                  isDark ? 'text-[#D4AF37]' : 'text-[#0E5A57]'
                }`}>VENTURES</span>
              </>
            )}
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7">
            <SolutionsMenu isDark={isDark} />
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={
                  link.label === 'About' ? NAV.aboutLink :
                  link.label === 'Insights' ? NAV.insightsLink :
                  link.label === 'Contact' ? NAV.contactLink : undefined
                }
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? isDark ? 'text-white' : 'text-[#0E5A57]'
                    : isDark ? 'text-white/75 hover:text-white' : 'text-[#374151] hover:text-[#0E5A57]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid={NAV.ctaBtn}
              className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 flex items-center gap-2 ${
                isDark
                  ? 'bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0A1628]'
                  : 'bg-[#0A1628] hover:bg-[#0F2847] text-white'
              }`}
            >
              Let's Talk <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid={NAV.mobileMenuBtn}
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors duration-150 ${
              isDark ? 'text-white hover:bg-white/10' : 'text-[#374151] hover:bg-[#F3F4F6]'
            }`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-white lg:hidden overflow-y-auto"
          >
            <div className="pt-[72px] pb-10 px-6">
              {/* Solutions Accordion */}
              <div className="border-b border-[#F3F4F6]">
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === 'solutions' ? null : 'solutions')}
                  className="w-full flex items-center justify-between py-4 text-base font-medium text-[#1F2937]"
                >
                  Solutions
                  <ChevronDown className={`w-4 h-4 text-[#9CA3AF] transition-transform duration-200 ${mobileExpanded === 'solutions' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {mobileExpanded === 'solutions' && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="pb-4 grid grid-cols-1 gap-2">
                        <Link to="/solutions/india" className="flex flex-col p-4 bg-[#0B3F3B] rounded-xl">
                          <span className="text-white font-semibold text-sm">India</span>
                          <span className="text-white/60 text-xs mt-0.5">Digital & Business Solutions</span>
                        </Link>
                        <Link to="/solutions/global" className="flex flex-col p-4 bg-[#0F2847] rounded-xl">
                          <span className="text-white font-semibold text-sm">Global</span>
                          <span className="text-white/60 text-xs mt-0.5">Outsourcing & Business Solutions</span>
                        </Link>
                        <Link to="/solutions" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E5A57] py-2">
                          View All Solutions <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Flat nav links */}
              {NAV_LINKS.map((link) => (
                <div key={link.href} className="border-b border-[#F3F4F6]">
                  <Link to={link.href} className="block py-4 text-base font-medium text-[#1F2937] hover:text-[#0E5A57] transition-colors duration-150">
                    {link.label}
                  </Link>
                </div>
              ))}

              <div className="mt-8 space-y-3">
                <Link to="/contact" className="block w-full text-center bg-[#0A1628] hover:bg-[#0F2847] text-white text-base font-semibold py-3.5 rounded-full transition-colors duration-200">
                  Let's Talk
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
